import { Dispatch, SetStateAction, useCallback } from "react";
import { GetAccount200ResponsePositionsInner } from "../../codegen-api";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useOrder } from "../../hooks/api/order/useOrder";
import { usePerpOrder } from "../../hooks/api/order/usePerpOrder";
import { useToast } from "../../hooks/toast";
import { useSFX } from "../../hooks/useSFX";
import { IBaseModalProps } from "../BaseModal";
import { runClosePosition } from "./ClosePositionModal.actions";
import { runExecutePerpClose } from "./ClosePositionModal.perpAction";

type TranslateFn = (...args: any[]) => any;
type AccountData = ReturnType<typeof useGetAccount>["data"];
type MutateAccount = ReturnType<typeof useGetAccount>["mutate"];
type AddToast = ReturnType<typeof useToast>["addToast"];
type PlaySound = ReturnType<typeof useSFX>["playSound"];
type ClosePosition = ReturnType<typeof useOrder>["closePosition"];
type ClosePerpPosition = ReturnType<typeof usePerpOrder>["closePerpPosition"];
type PlacePerpOrder = ReturnType<typeof usePerpOrder>["placePerpOrder"];
type CancelRestingCloseOrders =
  ReturnType<typeof usePerpOrder>["cancelRestingCloseOrders"];

interface IUseClosePositionSubmitParams {
  position: GetAccount200ResponsePositionsInner | undefined;
  closePerpPosition: ClosePerpPosition;
  closePosition: ClosePosition;
  placePerpOrder: PlacePerpOrder;
  cancelRestingCloseOrders: CancelRestingCloseOrders;
  accountData: AccountData;
  mutateAccount: MutateAccount;
  revalidateAfterPositionClose: () => void;
  onHide: IBaseModalProps["onHide"];
  playSound: PlaySound;
  addToast: AddToast;
  t: TranslateFn;
  apiError: TranslateFn;
  closeSizeInput: string;
  closeMode: "market" | "limit";
  limitPriceInput: string;
  setError: Dispatch<SetStateAction<string | undefined>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}

export const useClosePositionSubmit = ({
  position,
  closePerpPosition,
  closePosition,
  placePerpOrder,
  cancelRestingCloseOrders,
  accountData,
  mutateAccount,
  revalidateAfterPositionClose,
  onHide,
  playSound,
  addToast,
  t,
  apiError,
  closeSizeInput,
  closeMode,
  limitPriceInput,
  setError,
  setLoading,
}: IUseClosePositionSubmitParams) => {
  const onClosePosition = useCallback(async () => {
    await runClosePosition({
      position,
      closePerpPosition,
      closePosition,
      accountData,
      mutateAccount,
      revalidateAfterPositionClose,
      onHide,
      playSound,
      addToast,
      t,
      apiError,
      setError,
      setLoading,
    });
  }, [
    accountData,
    addToast,
    apiError,
    closePosition,
    closePerpPosition,
    mutateAccount,
    onHide,
    playSound,
    position,
    revalidateAfterPositionClose,
    t,
  ]);

  // Perp close: Market (direct on-chain reduce-only market close) or Limit
  // (opposite-side resting LIMIT order via placePerpOrder/openPosition).
  // Both are on-chain. Supports partial size (capped to the position).
  const executePerpClose = useCallback(async () => {
    await runExecutePerpClose({
      position,
      closeSizeInput,
      closeMode,
      accountData,
      cancelRestingCloseOrders,
      placePerpOrder,
      limitPriceInput,
      mutateAccount,
      revalidateAfterPositionClose,
      onHide,
      playSound,
      addToast,
      t,
      apiError,
      closePerpPosition,
      setError,
      setLoading,
    });
  }, [
    accountData,
    addToast,
    apiError,
    closeMode,
    closePerpPosition,
    cancelRestingCloseOrders,
    closeSizeInput,
    placePerpOrder,
    limitPriceInput,
    mutateAccount,
    onHide,
    playSound,
    position,
    revalidateAfterPositionClose,
    t,
  ]);

  return {
    onClosePosition,
    executePerpClose,
  };
};
